import asteroid1Glb from 'assets/glb/Asteroid_1.glb';
import asteroid2Glb from 'assets/glb/Asteroid_2.glb';
import asteroid3Glb from 'assets/glb/Asteroid_3.glb';
import asteroid4Glb from 'assets/glb/Asteroid_4.glb';
import asteroid5Glb from 'assets/glb/Asteroid_5.glb';
import asteroid6Glb from 'assets/glb/Asteroid_6.glb';
import asteroid7Glb from 'assets/glb/Asteroid_7.glb';
import asteroid8Glb from 'assets/glb/Asteroid_8.glb';
import asteroid9Glb from 'assets/glb/Asteroid_9.glb';
import planet0Glb from 'assets/glb/Planet0.glb';
import planet1Glb from 'assets/glb/Planet1.glb';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// --- Constants (exact copy from b6.html) ---

const PLANET_COUNT = 2;
const PLANET_AXIS_TILTS = [0.4, -0.3];
const RED_PLANET_INDEX = 1;

const PLANET_GLBS = [planet0Glb, planet1Glb];
const ASTEROID_FILES = [
  asteroid1Glb,
  asteroid2Glb,
  asteroid3Glb,
  asteroid4Glb,
  asteroid5Glb,
  asteroid6Glb,
  asteroid7Glb,
  asteroid8Glb,
  asteroid9Glb,
];
const ASTEROIDS_PER_FILE = 15;
const ASTEROID_RING = {
  radiusMinMul: 1.4,
  radiusMaxMul: 1.9,
  thickness: 0.6,
  radialJitter: 1.0,
  scaleMin: 0.35,
  scaleMax: 1.55,
  spinSpeed: 0.18,
};

const ORBIT_RADIUS = [95, 120];
const ORBIT_SPEED = [0.08, 0.03];
const ORBIT_HEIGHT = [2, -2];
const ORBIT_TILT = [0, 0];
const SELF_ROTATION_SPEED = [0.3, 0.2];
const PROCEDURAL_COLORS = [0xff6633, 0x3366ff];
const PLANET_SCALE = 12;

// Swap mode constants — desktop
const SWAP_POS = new THREE.Vector3(-300, 140, 210);
const SWAP_LEFT = new THREE.Vector3(-325, 150, 210);
const SWAP_RIGHT = new THREE.Vector3(-275, 150, 210);
const SWAP_CAMERA_POS = new THREE.Vector3(-300, 115, 180);

// Swap mode constants — mobile portrait (width < 768)
// Single red planet: centered, enlarged. Blue planet moved behind camera (not visible).
const SWAP_RED_M = new THREE.Vector3(-300, 162, 210);
const SWAP_BLUE_HIDDEN_M = new THREE.Vector3(-300, 150, 60); // behind camera (Z < camZ)
const SWAP_POS_M = new THREE.Vector3(-300, 142, 210); // camera look-at target
const SWAP_CAMERA_POS_M = new THREE.Vector3(-300, 115, 150); // closer for larger appearance
const SWAP_PLANET_SCALE_M = 1.4; // red planet scale multiplier
const ENTER_DURATION = 2400;
const EXIT_DURATION = 2000;

// --- Atmosphere shader (exact copy from b6.html) ---

const ATMOSPHERE_VERTEX = `
  varying vec3 vNormal;
  varying vec3 vWorldPos;
  varying vec3 vLocalPos;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    vLocalPos = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const ATMOSPHERE_FRAGMENT = `
  uniform vec3 glowColor;
  uniform vec3 cloudColor;
  uniform float time;
  varying vec3 vNormal;
  varying vec3 vWorldPos;
  varying vec3 vLocalPos;

  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = mod289(i);
    vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
  }

  void main() {
    vec3 viewDir = normalize(cameraPosition - vWorldPos);
    float fresnel = pow(1.0 - abs(dot(vNormal, viewDir)), 3.0);

    vec3 np = normalize(vLocalPos) * 2.5;
    float drift = time * 0.04;
    float n1 = snoise(np + vec3(drift, 0.0, -drift * 0.7)) * 0.6;
    float n2 = snoise(np * 2.2 + vec3(-drift * 0.5, drift * 0.3, drift * 0.8)) * 0.35;
    float lat = abs(normalize(vLocalPos).y);
    float band = smoothstep(0.05, 0.4, lat) * smoothstep(0.9, 0.5, lat);
    float clouds = max(0.0, n1 + n2) * band;
    clouds = smoothstep(0.05, 0.45, clouds);

    float atmoAlpha = fresnel * 0.4;
    float cloudAlpha = clouds * 0.3 * (0.4 + fresnel * 0.6);
    vec3 color = mix(glowColor, cloudColor, cloudAlpha / max(atmoAlpha + cloudAlpha, 0.001));
    float alpha = atmoAlpha + cloudAlpha;
    gl_FragColor = vec4(color, alpha);
  }
`;

// --- Helpers ---

const _rand = (min: number, max: number) => min + Math.random() * (max - min);

const updatePlanetOrbit = (planet: THREE.Group, index: number, angle: number) => {
  const r = ORBIT_RADIUS[index];
  const x = Math.cos(angle) * r;
  const z = Math.sin(angle) * r;
  const y = ORBIT_HEIGHT[index] + Math.sin(angle * 1.5) * 8 * ORBIT_TILT[index];
  planet.position.set(x, y, z);
};

const createProceduralPlanet = (index: number): THREE.Group => {
  const geometry = new THREE.SphereGeometry(6, 32, 32);
  const material = new THREE.MeshStandardMaterial({
    color: PROCEDURAL_COLORS[index],
    roughness: 0.7,
    metalness: 0.2,
  });
  const mesh = new THREE.Mesh(geometry, material);

  const axisGroup = new THREE.Group();
  axisGroup.rotation.z = PLANET_AXIS_TILTS[index];
  axisGroup.add(mesh);

  const planetGroup = new THREE.Group();
  planetGroup.add(axisGroup);

  const light = new THREE.DirectionalLight(0xffffff, 0.6);
  light.position.set(5, 10, 5);
  light.target.position.set(0, 0, 0);
  planetGroup.add(light);
  planetGroup.add(light.target);

  return planetGroup;
};

// --- Ember glow data ---
interface GlowEntry {
  mat: THREE.MeshStandardMaterial;
  base: number;
  amp: number;
  phase: number;
}

/**
 * Planets with orbits, asteroid ring, atmosphere, swap mode.
 * Ported from: usePlanets.ts, useSwapObject.ts
 */
export class PlanetsSystem {
  private planets: (THREE.Group | null)[] = [null, null];
  private orbitAngles: number[] = [0, Math.PI];
  private asteroidRingRotator: THREE.Group | null = null;
  private swapAnimId: number | null = null;

  private swapMode = false;

  // Saved camera for swap
  private savedCameraPosition: THREE.Vector3 | null = null;
  private savedCameraTarget: THREE.Vector3 | null = null;

  // Ember glow
  private redPlanetGlow: GlowEntry[] = [];
  private asteroidGlow: GlowEntry[] = [];
  private emberInitDone = false;
  private elapsed = 0;

  // Callbacks
  private onSwapEnter: (() => void) | null = null;
  private onSwapExit: (() => void) | null = null;

  private createdPlanets: THREE.Group[] = [];

  private ambientLight: THREE.AmbientLight;

  constructor(
    private scene: THREE.Scene,
    private camera: THREE.PerspectiveCamera,
    private controls: OrbitControls
  ) {
    // Soft fill so planet dark sides are not completely black
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
    this.scene.add(this.ambientLight);

    this.loadPlanets();
  }

  setSwapCallbacks(onEnter: () => void, onExit: () => void): void {
    this.onSwapEnter = onEnter;
    this.onSwapExit = onExit;
  }

  private loadPlanets(): void {
    const loader = new GLTFLoader();

    PLANET_GLBS.forEach((glbUrl, index) => {
      loader.load(
        glbUrl,
        (gltf) => {
          this.setupPlanetInGalaxy(gltf.scene, index);
        },
        undefined,
        () => {
          console.warn('Could not load planet ' + index + ', using procedural fallback');
          const group = createProceduralPlanet(index);
          updatePlanetOrbit(group, index, this.orbitAngles[index]);
          this.planets[index] = group;
          this.scene.add(group);
          this.createdPlanets.push(group);
        }
      );
    });
  }

  private setupPlanetInGalaxy(model: THREE.Object3D, index: number): void {
    // 1. Scale to PLANET_SCALE units
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = PLANET_SCALE / maxDim;
    model.scale.setScalar(scale);

    // 2. Center model
    const scaledBox = new THREE.Box3().setFromObject(model);
    const scaledCenter = scaledBox.getCenter(new THREE.Vector3());
    model.position.set(-scaledCenter.x, -scaledCenter.y, -scaledCenter.z);

    // 3. Remove emissive to prevent bloom
    model.traverse((child) => {
      if ((child as THREE.Mesh).isMesh && (child as THREE.Mesh).material) {
        const mats = Array.isArray((child as THREE.Mesh).material)
          ? ((child as THREE.Mesh).material as THREE.Material[])
          : [(child as THREE.Mesh).material as THREE.Material];
        mats.forEach((mat) => {
          const m = mat as THREE.MeshStandardMaterial;
          if (m.envMapIntensity !== undefined) m.envMapIntensity = 1.5;
          if (m.emissive) m.emissive.set(0x000000);
          if (m.emissiveIntensity !== undefined) m.emissiveIntensity = 0;
          m.needsUpdate = true;
        });
      }
    });

    // Compute radius for atmosphere/ring
    const finalBox = new THREE.Box3().setFromObject(model);
    const finalSize = finalBox.getSize(new THREE.Vector3());
    const planetRadius = Math.max(finalSize.x, finalSize.y, finalSize.z) / 2;

    // 4. Hierarchy: planetGroup → axisGroup → model
    const axisGroup = new THREE.Group();
    axisGroup.rotation.z = PLANET_AXIS_TILTS[index];
    axisGroup.add(model);

    // Atmosphere for Planet0 (blue planet)
    if (index === 0) {
      const atmoGeo = new THREE.SphereGeometry(planetRadius * 1.15, 48, 48);
      const atmoMat = new THREE.ShaderMaterial({
        uniforms: {
          glowColor: { value: new THREE.Color(0x40e0d0) },
          cloudColor: { value: new THREE.Color(0xffffff) },
          time: { value: 0 },
        },
        vertexShader: ATMOSPHERE_VERTEX,
        fragmentShader: ATMOSPHERE_FRAGMENT,
        transparent: true,
        blending: THREE.AdditiveBlending,
        side: THREE.FrontSide,
        depthWrite: false,
      });
      const atmosphere = new THREE.Mesh(atmoGeo, atmoMat);
      atmosphere.userData.isAtmosphere = true;
      axisGroup.add(atmosphere);
    }

    const planetGroup = new THREE.Group();
    planetGroup.add(axisGroup);

    // Directional light
    const planetLight = new THREE.DirectionalLight(0xffffff, 0.6);
    planetLight.position.set(5, 10, 5);
    // target must be in the scene graph for Three.js r182 to compute world direction
    planetLight.target.position.set(0, 0, 0);
    planetGroup.add(planetLight);
    planetGroup.add(planetLight.target);

    // Asteroid ring around red planet
    if (index === RED_PLANET_INDEX) {
      this.initAsteroidRing(
        planetGroup,
        PLANET_AXIS_TILTS[index],
        planetRadius,
        SELF_ROTATION_SPEED[index] * 2
      );
    }

    // Place on orbit
    updatePlanetOrbit(planetGroup, index, this.orbitAngles[index]);
    this.planets[index] = planetGroup;
    this.scene.add(planetGroup);
    this.createdPlanets.push(planetGroup);
  }

  private initAsteroidRing(
    parentPlanetGroup: THREE.Group,
    axisTiltZ: number,
    planetRadius: number,
    ringSpinSpeed: number
  ): void {
    const ringPivot = new THREE.Group();
    ringPivot.rotation.z = axisTiltZ;

    const rotator = new THREE.Group();
    rotator.userData.isAsteroidRingRotator = true;
    rotator.userData.spinSpeed = ringSpinSpeed;
    this.asteroidRingRotator = rotator;

    ringPivot.add(rotator);
    parentPlanetGroup.add(ringPivot);

    this.loadAsteroidRingModels(rotator, planetRadius);
  }

  private loadAsteroidRingModels(ringGroup: THREE.Group, planetRadius: number): void {
    const loader = new GLTFLoader();
    const ringRadiusMin = planetRadius * ASTEROID_RING.radiusMinMul;
    const ringRadiusMax = planetRadius * ASTEROID_RING.radiusMaxMul;

    ASTEROID_FILES.forEach((glbUrl) => {
      loader.load(
        glbUrl,
        (gltf) => {
          const base = gltf.scene;

          // Normalize base size to ~1 unit
          const box = new THREE.Box3().setFromObject(base);
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z) || 1;
          base.scale.setScalar(1.0 / maxDim);

          // Center at origin
          const centeredBox = new THREE.Box3().setFromObject(base);
          const center = centeredBox.getCenter(new THREE.Vector3());
          base.position.sub(center);

          // No bloom
          base.traverse((child) => {
            if ((child as THREE.Mesh).isMesh && (child as THREE.Mesh).material) {
              const mats = Array.isArray((child as THREE.Mesh).material)
                ? ((child as THREE.Mesh).material as THREE.Material[])
                : [(child as THREE.Mesh).material as THREE.Material];
              mats.forEach((mat) => {
                const m = mat as THREE.MeshStandardMaterial;
                if (m.emissive) m.emissive.set(0x000000);
                if (m.emissiveIntensity !== undefined) m.emissiveIntensity = 0;
                m.needsUpdate = true;
              });
            }
          });

          // Bake base's normalisation + centring transforms into mesh geometries
          // so each InstancedMesh only needs per-instance position/rotation/scale.
          base.updateWorldMatrix(true, true);

          // Pre-generate ASTEROIDS_PER_FILE instance matrices (same random transform
          // sequence as the original clone loop to keep visual parity).
          const dummy = new THREE.Object3D();
          dummy.matrixAutoUpdate = false;
          const matrices: THREE.Matrix4[] = [];
          for (let k = 0; k < ASTEROIDS_PER_FILE; k++) {
            const s = _rand(ASTEROID_RING.scaleMin, ASTEROID_RING.scaleMax);
            const theta = Math.random() * Math.PI * 2;
            const r =
              _rand(ringRadiusMin, ringRadiusMax) +
              _rand(-ASTEROID_RING.radialJitter, ASTEROID_RING.radialJitter);
            const y = _rand(-ASTEROID_RING.thickness, ASTEROID_RING.thickness);

            dummy.position.set(Math.cos(theta) * r, y, Math.sin(theta) * r);
            dummy.scale.setScalar(s / 1.5);
            dummy.lookAt(0, 0, 0);
            dummy.rotateZ(_rand(0, Math.PI * 2));
            dummy.rotateX(_rand(-0.25, 0.25));
            dummy.rotateY(_rand(-0.25, 0.25));
            dummy.position.x += _rand(-0.3, 0.3);
            dummy.position.z += _rand(-0.3, 0.3);
            dummy.updateMatrix();
            matrices.push(dummy.matrix.clone());
          }

          // One InstancedMesh per mesh primitive — 135 draw calls → 9 (one per GLB file)
          base.traverse((child) => {
            const mesh = child as THREE.Mesh;
            if (!mesh.isMesh) return;

            // Bake centering + normalisation into the geometry so the instance
            // matrix only needs to encode per-asteroid transform.
            const geom = mesh.geometry.clone();
            geom.applyMatrix4(mesh.matrixWorld);

            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            mats.forEach((mat) => {
              const instanced = new THREE.InstancedMesh(
                geom,
                mat as THREE.Material,
                ASTEROIDS_PER_FILE
              );
              for (let k = 0; k < ASTEROIDS_PER_FILE; k++) {
                instanced.setMatrixAt(k, matrices[k]);
              }
              instanced.instanceMatrix.needsUpdate = true;
              ringGroup.add(instanced);
            });
          });
        },
        undefined,
        (err) => {
          console.warn('Could not load asteroid:', glbUrl, err);
        }
      );
    });
  }

  private initEmberGlow(): boolean {
    const red = this.planets[RED_PLANET_INDEX];
    const rotator = this.asteroidRingRotator;
    if (!red || !rotator) return false;

    // Warm point light
    const warm = new THREE.PointLight(0xff6633, 2.2, 0);
    warm.position.set(20, 10, 16);
    red.add(warm);

    // Patchy embers on red planet meshes (28% probability)
    red.traverse((child) => {
      if (!(child as THREE.Mesh).isMesh || !(child as THREE.Mesh).material) return;
      const mats = Array.isArray((child as THREE.Mesh).material)
        ? ((child as THREE.Mesh).material as THREE.Material[])
        : [(child as THREE.Mesh).material as THREE.Material];

      const newMats = mats.map((mat) => {
        const ember = Math.random() < 0.28;
        const m = (mat as THREE.MeshStandardMaterial).clone();
        if (m.envMapIntensity !== undefined)
          m.envMapIntensity = Math.max(m.envMapIntensity || 0, 2.2);

        if (ember && m.emissive) {
          m.emissive.set(0x331100);
          m.emissiveIntensity = 0.22;
          this.redPlanetGlow.push({
            mat: m,
            base: 0.22,
            amp: 0.08,
            phase: Math.random() * Math.PI * 2,
          });
        } else {
          if (m.emissive) m.emissive.set(0x000000);
          m.emissiveIntensity = 0;
        }
        m.needsUpdate = true;
        return m;
      });

      (child as THREE.Mesh).material = Array.isArray((child as THREE.Mesh).material)
        ? newMats
        : newMats[0];
    });

    // Patchy embers on asteroids (18% probability)
    rotator.children.forEach((asteroid) => {
      if (Math.random() >= 0.18) return;

      asteroid.traverse((child) => {
        if (!(child as THREE.Mesh).isMesh || !(child as THREE.Mesh).material) return;
        const mats = Array.isArray((child as THREE.Mesh).material)
          ? ((child as THREE.Mesh).material as THREE.Material[])
          : [(child as THREE.Mesh).material as THREE.Material];

        const newMats = mats.map((mat) => {
          const m = (mat as THREE.MeshStandardMaterial).clone();
          if (m.envMapIntensity !== undefined)
            m.envMapIntensity = Math.max(m.envMapIntensity || 0, 1.8);
          if (m.emissive) {
            m.emissive.set(0x220800);
            m.emissiveIntensity = 0.18;
            this.asteroidGlow.push({
              mat: m,
              base: 0.18,
              amp: 0.07,
              phase: Math.random() * Math.PI * 2,
            });
          }
          m.needsUpdate = true;
          return m;
        });

        (child as THREE.Mesh).material = Array.isArray((child as THREE.Mesh).material)
          ? newMats
          : newMats[0];
      });
    });

    this.emberInitDone = true;
    return true;
  }

  tick(dt: number): void {
    this.elapsed += dt;

    // Try to init ember glow once models and asteroids are loaded
    if (!this.emberInitDone && this.asteroidRingRotator?.children.length) {
      this.initEmberGlow();
    }

    // Animate asteroid ring rotation (always, regardless of swap mode — matches original)
    const rotator = this.asteroidRingRotator;
    if (rotator) {
      rotator.rotation.y += (rotator.userData.spinSpeed || ASTEROID_RING.spinSpeed) * dt;
    }

    // Animate ember glow
    const t = this.elapsed;
    for (const g of this.redPlanetGlow) {
      g.mat.emissiveIntensity = g.base + Math.sin(t * 2.2 + g.phase) * g.amp;
    }
    for (const g of this.asteroidGlow) {
      g.mat.emissiveIntensity = g.base + Math.sin(t * 4.0 + g.phase) * g.amp;
    }

    for (let i = 0; i < PLANET_COUNT; i++) {
      const planet = this.planets[i];
      if (!planet) continue;

      const axisGroup = planet.children[0] as THREE.Group | undefined;
      if (!axisGroup) continue;

      if (this.swapMode) {
        // Only self-rotation in swap mode (half speed)
        axisGroup.rotation.y += SELF_ROTATION_SPEED[i] * dt * 0.5;

        // Keep atmosphere animating
        if (i === 0) {
          axisGroup.children.forEach((child) => {
            if (
              child.userData?.isAtmosphere &&
              (child as THREE.Mesh).material &&
              ((child as THREE.Mesh).material as THREE.ShaderMaterial).uniforms
            ) {
              ((child as THREE.Mesh).material as THREE.ShaderMaterial).uniforms.time.value += dt;
            }
          });
        }
        continue;
      }

      // Update orbit
      this.orbitAngles[i] += ORBIT_SPEED[i] * dt;
      updatePlanetOrbit(planet, i, this.orbitAngles[i]);

      // Self-rotation
      axisGroup.rotation.y += SELF_ROTATION_SPEED[i] * dt;

      // Update atmosphere
      if (i === 0) {
        axisGroup.children.forEach((child) => {
          if (
            child.userData?.isAtmosphere &&
            (child as THREE.Mesh).material &&
            ((child as THREE.Mesh).material as THREE.ShaderMaterial).uniforms
          ) {
            ((child as THREE.Mesh).material as THREE.ShaderMaterial).uniforms.time.value += dt;
          }
        });
      }
    }
  }

  enterSwapMode(): void {
    if (this.swapAnimId !== null) {
      cancelAnimationFrame(this.swapAnimId);
      this.swapAnimId = null;
    }

    this.swapMode = true;

    // Responsive positions: mobile shows only the red planet (index 1), enlarged + centered.
    // Blue planet (index 0) is moved behind the camera so it is not visible.
    const isMobile = window.innerWidth < 768;
    const swapLeft = isMobile ? SWAP_BLUE_HIDDEN_M : SWAP_LEFT; // planet 0 (blue)
    const swapRight = isMobile ? SWAP_RED_M : SWAP_RIGHT; // planet 1 (red)
    const swapCamPos = isMobile ? SWAP_CAMERA_POS_M : SWAP_CAMERA_POS;
    const swapPos = isMobile ? SWAP_POS_M : SWAP_POS;

    const startPositions = this.planets.map(
      (p, i) => p?.position.clone() ?? (i === 0 ? swapLeft.clone() : swapRight.clone())
    );

    const startCameraPos = this.camera.position.clone();
    const startCameraTarget = this.controls.target.clone();
    this.savedCameraPosition = startCameraPos;
    this.savedCameraTarget = startCameraTarget;

    // Allow camera to be below the orbit target — OrbitControls clamps polar angle otherwise
    this.controls.maxPolarAngle = Math.PI;
    this.controls.autoRotate = false;
    this.controls.enabled = false;

    const startTime = performance.now();

    // Compute start and target rotation.y and light positions for each planet
    const swapTargets = [swapLeft, swapRight];
    const startRotY: number[] = [];
    const deltaRotY: number[] = [];
    const startLightPos: THREE.Vector3[] = [];
    const targetLightPos: THREE.Vector3[] = [];
    for (let i = 0; i < PLANET_COUNT; i++) {
      const planet = this.planets[i];
      const axisGroup = planet?.children[0] as THREE.Group | undefined;
      const rotStart = axisGroup?.rotation.y ?? 0;
      startRotY.push(rotStart);

      // Direction from planet swap position to camera in XZ — determines facing angle
      const dir = swapCamPos.clone().sub(swapTargets[i]);
      const rotTarget = Math.atan2(-dir.x, -dir.z);
      // Shortest-path delta
      let delta = rotTarget - rotStart;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      deltaRotY.push(delta);

      // Light: capture current position and compute target toward camera
      const light = planet?.children.find((c) => c instanceof THREE.DirectionalLight) as
        | THREE.DirectionalLight
        | undefined;
      startLightPos.push(light?.position.clone() ?? new THREE.Vector3(5, 10, 5));
      targetLightPos.push(swapCamPos.clone().sub(swapTargets[i]).normalize().multiplyScalar(20));
    }

    const animateToSwap = () => {
      const progress = Math.min((performance.now() - startTime) / ENTER_DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      if (this.planets[0]) this.planets[0].position.lerpVectors(startPositions[0], swapLeft, eased);
      if (this.planets[1])
        this.planets[1].position.lerpVectors(startPositions[1], swapRight, eased);

      // Rotate planets to face camera and lerp light position toward camera direction
      for (let i = 0; i < PLANET_COUNT; i++) {
        const planet = this.planets[i];
        const axisGroup = planet?.children[0] as THREE.Group | undefined;
        if (axisGroup) axisGroup.rotation.y = startRotY[i] + deltaRotY[i] * eased;
        const light = planet?.children.find((c) => c instanceof THREE.DirectionalLight) as
          | THREE.DirectionalLight
          | undefined;
        if (light) light.position.lerpVectors(startLightPos[i], targetLightPos[i], eased);
      }

      this.camera.position.lerpVectors(startCameraPos, swapCamPos, eased);
      this.controls.target.lerpVectors(startCameraTarget, swapPos, eased);
      this.camera.lookAt(this.controls.target);

      // On mobile: animate red planet scale from 1 → SWAP_PLANET_SCALE_M
      if (isMobile && this.planets[1]) {
        this.planets[1].scale.setScalar(1 + (SWAP_PLANET_SCALE_M - 1) * eased);
      }

      if (progress < 1) {
        this.swapAnimId = requestAnimationFrame(animateToSwap);
      } else {
        this.swapAnimId = null;
        this.controls.enabled = true;
        this.controls.enableRotate = true;
        this.controls.enablePan = false;
        this.controls.enableZoom = true;
        this.onSwapEnter?.();
      }
    };

    this.swapAnimId = requestAnimationFrame(animateToSwap);
  }

  exitSwapMode(): void {
    if (this.swapAnimId !== null) {
      cancelAnimationFrame(this.swapAnimId);
      this.swapAnimId = null;
    }

    this.onSwapExit?.();

    const startPositions = this.planets.map(
      (p, i) => p?.position.clone() ?? (i === 0 ? SWAP_LEFT.clone() : SWAP_RIGHT.clone())
    );

    // Recalculate target orbit positions
    const restoreTargets: THREE.Vector3[] = [];
    for (let i = 0; i < PLANET_COUNT; i++) {
      const r = ORBIT_RADIUS[i];
      const angle = this.orbitAngles[i];
      const x = Math.cos(angle) * r;
      const z = Math.sin(angle) * r;
      const y = ORBIT_HEIGHT[i];
      restoreTargets.push(new THREE.Vector3(x, y, z));
    }

    const startCameraPos = this.camera.position.clone();
    const startCameraTarget = this.controls.target.clone();
    const restoreCameraPos = this.savedCameraPosition || new THREE.Vector3(0, 40, 80);
    const restoreCameraTarget = this.savedCameraTarget || new THREE.Vector3(0, 0, 0);

    // Capture red planet scale in case it was enlarged on mobile
    const startScale1 = this.planets[1]?.scale.x ?? 1.0;

    this.controls.enabled = false;

    // Capture current light positions to lerp back to default during exit
    const defaultLightPos = new THREE.Vector3(5, 10, 5);
    const startExitLightPos: THREE.Vector3[] = this.planets.map((planet) => {
      const light = planet?.children.find((c) => c instanceof THREE.DirectionalLight) as
        | THREE.DirectionalLight
        | undefined;
      return light?.position.clone() ?? defaultLightPos.clone();
    });

    const startTime = performance.now();

    const animateBack = () => {
      const progress = Math.min((performance.now() - startTime) / EXIT_DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      if (this.planets[0])
        this.planets[0].position.lerpVectors(startPositions[0], restoreTargets[0], eased);
      if (this.planets[1])
        this.planets[1].position.lerpVectors(startPositions[1], restoreTargets[1], eased);

      // Lerp lights back to default position
      for (let i = 0; i < PLANET_COUNT; i++) {
        const light = this.planets[i]?.children.find((c) => c instanceof THREE.DirectionalLight) as
          | THREE.DirectionalLight
          | undefined;
        if (light) light.position.lerpVectors(startExitLightPos[i], defaultLightPos, eased);
      }

      this.camera.position.lerpVectors(startCameraPos, restoreCameraPos, eased);
      this.controls.target.lerpVectors(startCameraTarget, restoreCameraTarget, eased);
      this.camera.lookAt(this.controls.target);

      // Restore red planet scale if it was enlarged on mobile
      if (startScale1 !== 1.0 && this.planets[1]) {
        this.planets[1].scale.setScalar(startScale1 + (1.0 - startScale1) * eased);
      }

      if (progress < 1) {
        this.swapAnimId = requestAnimationFrame(animateBack);
      } else {
        this.swapAnimId = null;
        this.swapMode = false;
        this.planets[1]?.scale.setScalar(1.0);
        this.controls.maxPolarAngle = Math.PI / 1.5;
        this.controls.autoRotate = true;
        this.controls.enabled = true;
        this.controls.enablePan = true;
      }
    };

    this.swapAnimId = requestAnimationFrame(animateBack);
  }

  destroy(): void {
    if (this.swapAnimId !== null) {
      cancelAnimationFrame(this.swapAnimId);
    }

    this.scene.remove(this.ambientLight);

    this.createdPlanets.forEach((p) => {
      this.scene.remove(p);
      p.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach((mat) => {
            if (mat instanceof THREE.Material) mat.dispose();
          });
        }
      });
    });
  }
}
